import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpaces } from '../queries/useCompanyQueries'
import { useSearchFormData, setSearchFormDataKey } from '../queries/useSearchState'
import { queryClient } from '../queries/config'

const SearchIFrame = () => {
  const [destinations, setDestinations] = useState<any>([])
  const [capacity, setCapacity] = useState<number>(0)

  const { data: spacesData } = useSpaces()
  const { data: searchFormData } = useSearchFormData()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const langParam = urlParams.get('lang')
    const language = langParam === 'es' ? 'es' : 'en'
    i18n.changeLanguage(language)
  }, [i18n])

  const handleChangeOrigin = (event: any) => {
    const { name, value } = event.target
    queryClient.setQueryData([setSearchFormDataKey], (old: FormData) => ({
      ...old,
      [name]: value
    }))
    setDestinations(spacesData?.body?.[value]?.areaDetailList || [])
  }

  const handleChangeDetination = (event: any) => {
    const { name, value } = event.target
    queryClient.setQueryData([setSearchFormDataKey], (old: FormData) => ({
      ...old,
      [name]: value
    }))
    setCapacity(
      spacesData?.body?.[searchFormData?.origen]?.areaDetailList?.find(
        (item: any) => item.description === value
      )?.capacity || 0
    )
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target
    queryClient.setQueryData([setSearchFormDataKey], (old: FormData) => ({
      ...old,
      [name]: value
    }))
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const searchParams = new URLSearchParams(searchFormData as unknown as Record<string, string>)
    window.open(`/?${searchParams.toString()}`, '_blank')
  }

  const inputWrapper = 'relative flex flex-col space-y-1'
  const label = 'text-green-50/90 text-sm font-medium tracking-wide'
  const input = `
    w-full h-12 px-4 py-2
    bg-green-900/30
    border border-green-800/50
    text-green-50 placeholder-green-200/30
    rounded-lg
    backdrop-blur-sm
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-green-700/50 focus:border-transparent
    hover:bg-green-900/40
  `
  const selectIcon = `
    absolute right-4 top-[38px] 
    w-0 h-0 
    border-l-[4px] border-l-transparent 
    border-t-[4px] border-t-green-300/50 
    border-r-[4px] border-r-transparent 
    pointer-events-none
    transition-transform duration-200
    group-hover:border-t-green-200/70
  `

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-800/30 to-green-900/30">
      <div className="w-full h-full backdrop-blur-[2px]">
        <div className="w-full h-full bg-green-950/70 p-4 sm:p-6 shadow-2xl">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            {/* Grid de campos */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-grow">
              {/* Origen */}
              <div className={`${inputWrapper} group`}>
                <label className={label}>{t('origin')}</label>
                <select
                  name="origen"
                  value={searchFormData?.origen || ''}
                  onChange={handleChangeOrigin}
                  required
                  className={`${input} appearance-none cursor-pointer`}
                >
                  <option value="" className="bg-green-950">
                    {t('selectOrigin')}
                  </option>
                  {spacesData &&
                    Object.values(spacesData.body.areas).map((item: any) => (
                      <option key={item} value={item} className="bg-green-950">
                        {item}
                      </option>
                    ))}
                </select>
                <div className={selectIcon} />
              </div>

              {/* Destino */}
              <div className={`${inputWrapper} group`}>
                <label className={label}>{t('destination')}</label>
                <select
                  name="destino"
                  value={searchFormData?.destino || ''}
                  onChange={handleChangeDetination}
                  required
                  disabled={!searchFormData?.origen}
                  className={`${input} appearance-none cursor-pointer ${
                    !searchFormData?.origen && 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <option value="" className="bg-green-950">
                    {t('selectDestination')}
                  </option>
                  {destinations.map((dest: any) => (
                    <option
                      key={dest.description}
                      value={dest.description}
                      className="bg-green-950"
                    >
                      {dest.description}
                    </option>
                  ))}
                </select>
                <div className={selectIcon} />
              </div>

              {/* Fecha */}
              <div className={inputWrapper}>
                <label className={label}>{t('date')}</label>
                <input
                  type="date"
                  name="date"
                  value={searchFormData?.date || ''}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className={`${input} [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-40 cursor-pointer`}
                />
              </div>

              {/* Pasajeros */}
              <div className={`${inputWrapper} group`}>
                <label className={label}>{t('passengers')}</label>
                <select
                  name="passengers"
                  value={searchFormData?.passengers || '1'}
                  onChange={handleChange}
                  required
                  className={`${input} appearance-none cursor-pointer`}
                >
                  {Array.from({ length: capacity }, (_, i) => i + 1).map((num) => (
                    <option key={num} value={num} className="bg-green-950">
                      {num} {t(num > 1 ? 'passengers_plural' : 'passenger')}
                    </option>
                  ))}
                </select>
                <div className={selectIcon} />
              </div>
            </div>

            {/* Botón de búsqueda */}
            <div className="flex justify-center mt-4">
              <button
                type="submit"
                className="
                  group relative
                  w-full md:w-auto px-8 py-3
                  bg-[#f9dc0d]
                  text-green-950 font-bold
                  rounded-lg
                  transition-all duration-300
                  shadow-lg shadow-amber-900/30
                  hover:shadow-amber-900/40
                  focus:outline-none focus:ring-2 focus:ring-amber-500/50
                  min-w-[200px]
                  border border-amber-400/20
                  hover:scale-[1.02]
                  active:scale-[0.98]
                "
              >
                <span className="relative inline-flex items-center justify-center gap-2">
                  <span>{t('search')}</span>
                  <svg
                    className="w-4 h-4 transition-transform duration-200 transform group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SearchIFrame
